// src/features/game/pages/CreateTemplatePage.tsx

import React, {
  useState,
} from 'react';

import {
  useNavigate,
} from 'react-router-dom';

import {
  ArrowLeft,
} from 'lucide-react';

import {
  useModal,
} from '../../../shared/components/ModalProvider';

import {
  createGameTemplate,
} from '../services/gameService';

import type {
  Blind,
  CreateTemplateRequest,
  ReEntry,
} from '../types/game.types';

import TemplateFormFields from '../components/TemplateFormFields';
import BlindListEditor from '../components/BlindListEditor';
import ReEntryListEditor from '../components/ReEntryListEditor';

import RouteConfig from '../../../routes/RouteConfig';

type TemplateFormData = Omit<
  CreateTemplateRequest,
  'blinds' | 'reEntries'
>;

type TemplateFormErrors =
  Partial<
    Record<
      keyof TemplateFormData,
      string
    >
  >;

const INITIAL_FORM_DATA: TemplateFormData = {
  gameName: '',
  gameStack: 35000,
  gamePrice: 10000,
  gameLimit: 10,
  buyinEndLevel: 6,
  gameEndLevel: 12,
  earlyAmount: 0,
};

const INITIAL_BLINDS: Blind[] = [
  {
    level: 1,
    smallBlind: 100,
    bigBlind: 200,
    ante: 0,
    duration: 10,
    isBreak: false,
  },
];

const CreateTemplatePage: React.FC = () => {
  const navigate =
    useNavigate();

  const modal =
    useModal();

  /**
   * ==============================
   * 기본 정보
   * ==============================
   */
  const [
    formData,
    setFormData,
  ] =
    useState<TemplateFormData>(
      INITIAL_FORM_DATA,
    );

  /**
   * ==============================
   * 블라인드
   * ==============================
   */
  const [
    blinds,
    setBlinds,
  ] =
    useState<Blind[]>(
      INITIAL_BLINDS,
    );

  /**
   * ==============================
   * 리엔트리
   * ==============================
   */
  const [
    reEntries,
    setReEntries,
  ] =
    useState<ReEntry[]>(
      [],
    );

  /**
   * ==============================
   * 상태
   * ==============================
   */
  const [
    loading,
    setLoading,
  ] =
    useState(false);

  const [
    errors,
    setErrors,
  ] =
    useState<TemplateFormErrors>(
      {},
    );

  /**
   * ==============================
   * 게임 생성 페이지로 이동
   * ==============================
   *
   * TemplatesPage를 제거했기 때문에
   * 템플릿 생성/수정 관리는
   * CreateGamePage에서 담당합니다.
   */
  const navigateToCreateGame =
    (): void => {
      navigate(
        RouteConfig.Management
          .GameCreate.fullPath,
      );
    };

  /**
   * ==============================
   * 기본 정보 변경
   * ==============================
   */
  const handleFieldChange = (
    field: keyof TemplateFormData,
    value: string | number,
  ): void => {
    setFormData(
      (previous) => ({
        ...previous,
        [field]: value,
      }),
    );

    /**
     * 해당 필드 에러 제거
     */
    setErrors(
      (previous) => {
        if (
          !previous[field]
        ) {
          return previous;
        }

        return {
          ...previous,
          [field]: undefined,
        };
      },
    );
  };

  /**
   * ==============================
   * Validation
   * ==============================
   */
  const validate =
    async (): Promise<boolean> => {
      const newErrors:
        TemplateFormErrors = {};

      if (
        !formData.gameName.trim()
      ) {
        newErrors.gameName =
          '게임명을 입력해주세요.';
      }

      if (
        formData.gameStack <= 0
      ) {
        newErrors.gameStack =
          '시작 스택은 0보다 커야 합니다.';
      }

      if (
        formData.gamePrice <= 0
      ) {
        newErrors.gamePrice =
          '바이인 금액은 0보다 커야 합니다.';
      }

      if (
        formData.gameLimit <= 0
      ) {
        newErrors.gameLimit =
          '바이인 제한은 1회 이상이어야 합니다.';
      }

      if (
        formData.buyinEndLevel <= 0
      ) {
        newErrors.buyinEndLevel =
          '바이인 종료 레벨은 1 이상이어야 합니다.';
      }

      if (
        formData.gameEndLevel <= 0
      ) {
        newErrors.gameEndLevel =
          '게임 종료 레벨은 1 이상이어야 합니다.';
      }

      if (
        formData.buyinEndLevel >
        formData.gameEndLevel
      ) {
        newErrors.buyinEndLevel =
          '바이인 종료 레벨은 게임 종료 레벨보다 작거나 같아야 합니다.';
      }

      if (
        formData.earlyAmount < 0
      ) {
        newErrors.earlyAmount =
          '얼리칩은 0 이상이어야 합니다.';
      }

      setErrors(
        newErrors,
      );

      if (
        Object.keys(
          newErrors,
        ).length > 0
      ) {
        return false;
      }

      /**
       * 블라인드 최소 1개
       */
      if (
        blinds.length === 0
      ) {
        await modal.alert(
          '블라인드를 최소 1개 이상 추가해주세요.',
        );

        return false;
      }

      /**
       * 블라인드 검증
       */
      for (
        const blind of blinds
      ) {
        const isBreak =
          blind.isBreak === true ||
          (
            blind.smallBlind === null &&
            blind.bigBlind === null
          );

        if (
          blind.level <= 0
        ) {
          await modal.alert(
            '블라인드 레벨은 1 이상이어야 합니다.',
          );

          return false;
        }

        if (
          blind.duration <= 0
        ) {
          await modal.alert(
            `Level ${blind.level}: 시간은 1분 이상이어야 합니다.`,
          );

          return false;
        }

        if (isBreak) {
          continue;
        }

        if (
          blind.smallBlind === null ||
          blind.bigBlind === null
        ) {
          await modal.alert(
            `Level ${blind.level}: 스몰 블라인드와 빅 블라인드를 입력해주세요.`,
          );

          return false;
        }

        if (
          blind.smallBlind < 0 ||
          blind.bigBlind < 0
        ) {
          await modal.alert(
            `Level ${blind.level}: 블라인드는 0 이상이어야 합니다.`,
          );

          return false;
        }

        if (
          blind.bigBlind <=
          blind.smallBlind
        ) {
          await modal.alert(
            `Level ${blind.level}: 빅 블라인드는 스몰 블라인드보다 커야 합니다.`,
          );

          return false;
        }

        if (
          blind.ante !== null &&
          blind.ante < 0
        ) {
          await modal.alert(
            `Level ${blind.level}: 앤티는 0 이상이어야 합니다.`,
          );

          return false;
        }
      }

      /**
       * 중복 블라인드 레벨 검사
       */
      const levels =
        blinds.map(
          (blind: Blind) =>
            blind.level,
        );

      if (
        new Set(
          levels,
        ).size !==
        levels.length
      ) {
        await modal.alert(
          '중복된 블라인드 레벨이 있습니다.',
        );

        return false;
      }

      /**
       * 리엔트리 검사
       */
      for (
        const reEntry of
        reEntries
      ) {
        if (
          reEntry.count <= 0
        ) {
          await modal.alert(
            '리엔트리 횟수는 1 이상이어야 합니다.',
          );

          return false;
        }

        if (
          reEntry.price <= 0
        ) {
          await modal.alert(
            `${reEntry.count}번째 리엔트리 금액은 0보다 커야 합니다.`,
          );

          return false;
        }

        if (
          reEntry.stack <= 0
        ) {
          await modal.alert(
            `${reEntry.count}번째 리엔트리 스택은 0보다 커야 합니다.`,
          );

          return false;
        }
      }

      return true;
    };

  /**
   * ==============================
   * 템플릿 생성
   * ==============================
   */
  const handleCreate =
    async (): Promise<void> => {
      const isValid =
        await validate();

      if (!isValid) {
        return;
      }

      const gameName =
        formData.gameName.trim();

      const confirmed =
        await modal.confirm(
          `"${gameName}" 템플릿을 생성하시겠습니까?`,
        );

      if (!confirmed) {
        return;
      }

      const request:
        CreateTemplateRequest = {
        ...formData,

        gameName,

        blinds,

        reEntries,
      };

      try {
        setLoading(
          true,
        );

        await createGameTemplate(
          request,
        );

        await modal.alert(
          '템플릿이 생성되었습니다.',
        );

        /**
         * 템플릿 목록 페이지가 아니라
         * 게임 생성 페이지로 복귀
         */
        navigateToCreateGame();
      } catch (
        error: unknown
      ) {
        console.error(
          '게임 템플릿 생성 실패:',
          error,
        );

        await modal.alert(
          '템플릿 생성에 실패했습니다.',
        );
      } finally {
        setLoading(
          false,
        );
      }
    };

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-6 lg:p-8">
      <div className="mx-auto max-w-4xl">

        {/* Header */}
        <header className="mb-8 flex items-center gap-4">
          <button
            type="button"
            onClick={
              navigateToCreateGame
            }
            disabled={
              loading
            }
            className="grid h-10 w-10 place-items-center rounded-lg text-gray-600 transition hover:bg-gray-100 hover:text-gray-900 disabled:opacity-50"
            aria-label="게임 생성으로 돌아가기"
          >
            <ArrowLeft
              size={24}
            />
          </button>

          <div>
            <h1 className="text-2xl font-semibold text-gray-900 sm:text-3xl">
              템플릿 생성
            </h1>

            <p className="mt-1 text-sm text-gray-500">
              게임 기본 정보와 블라인드,
              리엔트리 구조를 설정합니다.
            </p>
          </div>
        </header>

        <div className="space-y-6">

          {/* 1. 기본 정보 */}
          <section className="rounded-xl bg-white p-5 shadow-sm sm:p-6">
            <h2 className="mb-4 text-lg font-semibold text-gray-900 sm:text-xl">
              1. 기본 정보
            </h2>

            <TemplateFormFields
              formData={
                formData
              }
              onChange={
                handleFieldChange
              }
              errors={
                errors
              }
            />
          </section>

          {/* 2. 블라인드 */}
          <section className="rounded-xl bg-white p-5 shadow-sm sm:p-6">
            <h2 className="mb-4 text-lg font-semibold text-gray-900 sm:text-xl">
              2. 블라인드 구조
            </h2>

            <BlindListEditor
              blinds={
                blinds
              }
              onChange={
                setBlinds
              }
            />
          </section>

          {/* 3. 리엔트리 */}
          <section className="rounded-xl bg-white p-5 shadow-sm sm:p-6">
            <h2 className="mb-4 text-lg font-semibold text-gray-900 sm:text-xl">
              3. 리엔트리 옵션
            </h2>

            <ReEntryListEditor
              reEntries={
                reEntries
              }
              onChange={
                setReEntries
              }
            />
          </section>

          {/* Actions */}
          <footer className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">

            <button
              type="button"
              onClick={
                navigateToCreateGame
              }
              disabled={
                loading
              }
              className="rounded-lg bg-gray-200 px-6 py-3 text-sm font-medium text-gray-700 transition hover:bg-gray-300 disabled:cursor-not-allowed disabled:opacity-50 sm:text-base"
            >
              취소
            </button>

            <button
              type="button"
              onClick={() =>
                void handleCreate()
              }
              disabled={
                loading
              }
              className="rounded-lg bg-main px-6 py-3 text-sm font-medium text-white transition hover:bg-main-medium disabled:cursor-not-allowed disabled:opacity-50 sm:text-base"
            >
              {loading
                ? '생성 중...'
                : '생성'}
            </button>

          </footer>
        </div>
      </div>
    </div>
  );
};

export default CreateTemplatePage;